import type { ExpressTool } from '@/api'
import { docsUrl } from '@/lib/docs'

/**
 * What the Express directory shows of a long list. A directory of a hundred tools is first its featured ones and
 * the ones already connected; the rest are a search, a category or "Show all" away. Nothing here knows any tool:
 * which are featured, and what they are filed under, is on each tool's own record.
 */
export type DirectoryView = {
    /** What the person typed to search by. */
    query: string
    /** The category chosen, or empty for every category. */
    category: string
    /** Whether the person asked for the whole list. */
    showAll: boolean
}

/**
 * The page the public site builds for a tool, from its tool type alone: every Live tool has one there, written from
 * the tool's own record, so the address needs no setting up.
 */
export function expressToolPage(slug: string): string {
    return docsUrl(`/docs/tools/${slug.trim().toLowerCase()}`)
}

/** Where a tool's docs are: the address its record names, else its own page on the public site. */
export function expressDocsPage(tool: Pick<ExpressTool, 'slug' | 'docsUrl'>): string {
    return tool.docsUrl || expressToolPage(tool.slug)
}

/**
 * What a person would say to their COO to have a task made with a tool: the tool's example task, with what the
 * proposal should cover spelled out. It is put in the message box of a new create-a-task conversation for the person
 * to read, change and send; the COO asks what is still missing, and the OS proposes the task for sign-off. Null
 * for a tool with no example task.
 */
export function exampleTaskRequest(tool: Pick<ExpressTool, 'slug' | 'name' | 'exampleTask'>): string | null {
    if (!tool.exampleTask) {
        return null
    }
    return [
        `I would like a new task that uses our ${tool.name} connection (its tools start with ${tool.slug}).`,
        '',
        `What it should do: ${tool.exampleTask}`,
        '',
        'Please propose the whole task for me to sign off:',
        '- a name, and the team and role that should own it',
        '- its goal, and how we will tell it was done well',
        '- three to six guardrails written for this job',
        `- only the ${tool.name} tools it needs, with my approval required before any that changes, sends or deletes something`,
        '- when it runs, and what it needs each time',
        '- the full instructions the member follows',
    ].join('\n')
}

/** Where the portal opens a create-a-task conversation with a tool's example task written out. */
export function exampleTaskChat(slug: string): { path: string; query: Record<string, string> } {
    return { path: '/chat', query: { purpose: 'create_task', tool: slug } }
}

/** A directory this short is shown whole, with no search or filter to work. */
export const SHORT_DIRECTORY = 8

/** The categories the tools are filed under, in alphabetical order. */
export function categoriesOf(tools: readonly ExpressTool[]): string[] {
    return [...new Set(tools.flatMap(tool => (tool.category ? [tool.category] : [])))].sort((a, b) =>
        a.localeCompare(b),
    )
}

/** Whether every word typed is somewhere in what a tool is called, does, is filed under or is also known by. */
function matches(tool: ExpressTool, query: string): boolean {
    const haystack = [tool.name, tool.description, tool.category, tool.keywords, tool.slug].join(' ').toLowerCase()
    return query
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every(word => haystack.includes(word))
}

/**
 * The tools to draw, in the order given, and how many more a "Show all" would add. Searching or choosing a category
 * looks through the whole list. Otherwise a long list is cut to its featured tools and the connected ones, unless
 * nothing is featured, in which case there is no shorter list to offer. `pinned` tools count as featured: the
 * onboarding tools step pins the common ones (#1942).
 */
export function directoryOf(
    tools: readonly ExpressTool[],
    view: DirectoryView,
    connected: (tool: ExpressTool) => boolean,
    pinned: (tool: ExpressTool) => boolean = () => false,
): { shown: ExpressTool[]; more: number } {
    const found = tools.filter(tool => matches(tool, view.query) && (!view.category || tool.category === view.category))
    const searching = view.query.trim() !== '' || view.category !== ''
    const isFeatured = (tool: ExpressTool) => (tool.rank ?? 0) > 0 || pinned(tool)
    const featured = tools.some(isFeatured)
    if (searching || view.showAll || tools.length <= SHORT_DIRECTORY || !featured) {
        return { shown: found, more: 0 }
    }
    const shown = found.filter(tool => isFeatured(tool) || connected(tool))
    return { shown, more: found.length - shown.length }
}
